import * as THREE from "three";
import {GetMapObject} from "../../Server/DB/mapObjects.js";
import {ResolveObjPosInWorld, ResolveObjRotInWorld} from "../../Server/DB/mapObjects/$mapObject.js";
import type {MapObject} from "../../Server/DB/mapObjects/@MapObject.js";
import {Mod_Viewpoint} from "../../Server/DB/mapObjects/@MapObject/ObjModifiers/Mod_Viewpoint.js";
import {buildPerspectiveCamera, viewpointEyeFromAnchor} from "./TerrainViewInfo.js";

/** Set15 P1 — resolve the ACTIVE `Mod_Viewpoint` into a `PerspectiveCamera`. Kept separate from
 *  the pure `TerrainViewInfo` seam because this module reaches into the DB accessors (GetMapObject
 *  / ResolveObjPosInWorld), which would drag the whole store into a pure-math test.
 *
 *  `MapSurface` calls `resolveViewpointCamera` to build the `<Canvas key="3d">` camera; the eye
 *  height is added over the terrain height at the host anchor (P1 passes 0 — terrain is flat; P3+
 *  threads the real height lattice). */

/** Resolve the active `Mod_Viewpoint` from a map. Returns the object + its modifier, or null if
 *  the active-object id doesn't name a viewpoint-bearing object. */
export function resolveActiveViewpoint(mapID: string, activeViewpointObjID: string|n): {obj: MapObject, mod: Mod_Viewpoint}|null {
	if (!activeViewpointObjID) return null;
	const obj = GetMapObject.CatchBail(undefined, activeViewpointObjID, mapID) as MapObject|n;
	if (!obj) return null;
	const mod = (obj.modifiers ?? []).find(m=>m.enabled && m.viewpoint)?.viewpoint;
	if (!mod) return null;
	return {obj, mod};
}

/** Resolve the active viewpoint's `PerspectiveCamera` (eye from the host object's world anchor +
 *  the BAKED absolute eye height, orientation from the modifier). Returns null when no active
 *  viewpoint is usable — the caller falls back to the "place a viewpoint" hint.  Set15 P5: the
 *  eye z = `mod.eyeHeight` (absolute), so it does NOT depend on the (churny) terrain height at the
 *  anchor — the camera position is stable by construction. */
export function resolveViewpointCamera(mapID: string, activeViewpointObjID: string|n): THREE.PerspectiveCamera|null {
	const vp = resolveActiveViewpoint(mapID, activeViewpointObjID);
	if (!vp) return null;
	const anchor = ResolveObjPosInWorld(vp.obj);
	if (!anchor) return null;
	const eye = viewpointEyeFromAnchor(anchor, vp.mod.eyeHeight);
	// The modifier's yaw is in the container object's LOCAL frame; add the container's world
	// rotation (itself + all ancestors, same clockwise-from-+y convention) so a rotated container
	// doesn't spin the resulting viewport relative to the world.
	const yaw = vp.mod.yaw + (ResolveObjRotInWorld(vp.obj) ?? 0);
	return buildPerspectiveCamera(eye, {yaw, pitch: vp.mod.pitch, fov: vp.mod.fov, roll: vp.mod.roll});
}